// 🔧 SOLUTION: Store files in Firestore using chunks (no Storage needed!)
// This avoids the 1MB Firestore document limit by splitting files

import { collection, doc, setDoc, getDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Upload a file to Firestore by splitting it into chunks
 * Works with free Firebase plan (no Storage needed)
 */
export const uploadFileToFirestore = async (file, bookId) => {
  try {
    console.log('📤 Starting chunked upload for:', file.name);
    
    // Convert file to base64
    const base64 = await fileToBase64(file);
    
    // Split into chunks (each chunk ~900KB to stay under 1MB limit)
    const CHUNK_SIZE = 900000; // 900KB per chunk
    const chunks = [];
    
    for (let i = 0; i < base64.length; i += CHUNK_SIZE) {
      chunks.push(base64.slice(i, i + CHUNK_SIZE));
    }
    
    console.log(`Splitting into ${chunks.length} chunks...`);
    
    // Store metadata
    const metadataRef = doc(db, 'bookFiles', bookId);
    await setDoc(metadataRef, {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      totalChunks: chunks.length,
      uploadedAt: new Date().toISOString()
    });
    
    // Store each chunk
    for (let i = 0; i < chunks.length; i++) {
      const chunkRef = doc(db, 'bookFiles', bookId, 'chunks', `chunk_${i}`);
      await setDoc(chunkRef, {
        data: chunks[i],
        index: i
      });
      console.log(`Uploaded chunk ${i + 1}/${chunks.length}`);
    }
    
    console.log('✅ File uploaded successfully!');
    return { success: true, bookId, fileName: file.name };
    
  } catch (error) {
    console.error('❌ Upload error:', error);
    throw error;
  }
};

/**
 * Download a file from Firestore by reassembling chunks
 */
export const downloadFileFromFirestore = async (bookId) => {
  try {
    console.log('📥 Starting chunked download for book:', bookId);
    
    // Get metadata
    const metadataRef = doc(db, 'bookFiles', bookId);
    const metadataDoc = await getDoc(metadataRef);
    
    if (!metadataDoc.exists()) {
      throw new Error('File not found');
    }
    
    const metadata = metadataDoc.data();
    console.log(`Downloading ${metadata.totalChunks} chunks...`);
    
    // Get all chunks
    const chunksRef = collection(db, 'bookFiles', bookId, 'chunks');
    const chunksSnapshot = await getDocs(chunksRef);
    
    // Sort chunks by index
    const sortedChunks = chunksSnapshot.docs
      .map(doc => ({ ...doc.data(), id: doc.id }))
      .sort((a, b) => a.index - b.index);
    
    // Reassemble base64
    const base64 = sortedChunks.map(chunk => chunk.data).join('');
    
    console.log('✅ File reassembled!');
    
    return {
      base64Data: base64,
      fileName: metadata.fileName,
      fileType: metadata.fileType
    };
    
  } catch (error) {
    console.error('❌ Download error:', error);
    throw error;
  }
};

/**
 * Helper: Convert File to base64
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Remove data URI prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Helper: Trigger download in browser
 */
export const triggerFileDownload = (base64Data, fileName, fileType) => {
  // Add data URI prefix back
  const dataURI = `data:${fileType};base64,${base64Data}`;
  
  const link = document.createElement('a');
  link.href = dataURI;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  
  setTimeout(() => {
    document.body.removeChild(link);
  }, 100);
};


  
  